import React, { useEffect, useState } from 'react';
import { useHouseStore } from '../store/houseStore';
import HouseCard from '../components/HouseCard';
import { Link } from 'react-router-dom';
import { Plus, Loader2, Map as MapIcon, List, Eye, Activity, Archive } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for Leaflet default icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const Home: React.FC = () => {
  const { houses, fetchHouses, loading } = useHouseStore();
  const [view, setView] = useState<'list' | 'map'>('list');
  const [filter, setFilter] = useState<'active' | 'watching' | 'inactive'>('active');

  useEffect(() => {
    fetchHouses();
  }, [fetchHouses]);

  const filteredHouses = houses.filter(h => (h.status || 'active') === filter);

  if (loading && houses.length === 0) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (houses.length === 0) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">No houses tracked yet</h2>
        <p className="text-gray-600 mb-8">Start by adding a property URL to track.</p>
        <Link
          to="/add"
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add First House
        </Link>
      </div>
    );
  }

  // Calculate map center
  const validCoords = filteredHouses.filter(h => h.lat && h.long);
  const center: [number, number] = validCoords.length > 0 
    ? [validCoords[0].lat!, validCoords[0].long!] 
    : [-41.2865, 174.7762]; // Default to Wellington

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-900">My Tracked Houses</h1>
        
        <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setView('list')}
            className={`p-2 rounded-md transition-all ${view === 'list' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
            title="List View"
          >
            <List className="w-5 h-5" />
          </button>
          <button
            onClick={() => setView('map')}
            className={`p-2 rounded-md transition-all ${view === 'map' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
            title="Map View"
          >
            <MapIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex overflow-x-auto sm:space-x-1 no-scrollbar">
          <button
            onClick={() => setFilter('active')}
            className={`flex-1 sm:flex-none flex items-center justify-center px-4 py-3 sm:py-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
              filter === 'active'
                ? 'border-indigo-500 text-indigo-600 bg-indigo-50 sm:bg-transparent'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Activity className="w-4 h-4 mr-2" />
            Active
            <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
              {houses.filter(h => (h.status || 'active') === 'active').length}
            </span>
          </button>
          <button
            onClick={() => setFilter('watching')}
            className={`flex-1 sm:flex-none flex items-center justify-center px-4 py-3 sm:py-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
              filter === 'watching'
                ? 'border-indigo-500 text-indigo-600 bg-indigo-50 sm:bg-transparent'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Eye className="w-4 h-4 mr-2" />
            Watching
            <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
              {houses.filter(h => h.status === 'watching').length}
            </span>
          </button>
          <button
            onClick={() => setFilter('inactive')}
            className={`flex-1 sm:flex-none flex items-center justify-center px-4 py-3 sm:py-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
              filter === 'inactive'
                ? 'border-indigo-500 text-indigo-600 bg-indigo-50 sm:bg-transparent'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Archive className="w-4 h-4 mr-2" />
            Inactive
            <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
              {houses.filter(h => h.status === 'inactive').length}
            </span>
          </button>
        </div>
      </div>

      {view === 'list' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredHouses.map((house) => (
            <HouseCard key={house.id} house={house} />
          ))}
          {filteredHouses.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              No properties found in this category.
            </div>
          )}
        </div>
      ) : (
        <div className="h-[600px] rounded-xl overflow-hidden shadow-lg border border-gray-200 z-0 relative">
          <MapContainer center={center} zoom={13} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filteredHouses.map((house) => (
              house.lat && house.long && (
                <Marker key={house.id} position={[house.lat, house.long]}>
                  <Popup>
                    <div className="w-48">
                      <h3 className="font-bold text-sm mb-1">{house.address}</h3>
                      <p className="text-xs text-gray-600 mb-2">
                        {new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', maximumFractionDigits: 0 }).format(house.estimate)}
                      </p>
                      <Link to={`/edit/${house.id}`} className="text-indigo-600 text-xs hover:underline">
                        View Details
                      </Link>
                    </div>
                  </Popup>
                </Marker>
              )
            ))}
          </MapContainer>
        </div>
      )}
    </div>
  );
};

export default Home;
